import React, { useContext, useRef, useCallback } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { useParams } from 'react-router-dom';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import IntentsAutoComplete from '../../../../components/IntentsAutoComplete/IntentsAutoComplete';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import GoToIntentTreeNode from '../../../../types/BotBuilder/GoToIntentTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import SlotMappingConfiguration from './SlotMappingConfiguration';
import SlotMapping from '../../../../types/BotBuilder/SlotMapping';
import BotBuilderService from '../../../../services/BotBuilderService/BotBuilderService';

export type GoToIntentNodeData = {
  node: BotResponseTreeNode;
  isLocked?: boolean;
};

function GoToIntentNode(
  props: NodeProps<GoToIntentNodeData>
): React.ReactElement {
  const { data, isConnectable, id } = props;
  const { chatbotId } = useParams();
  const { onNodeDelete, onNodeUpdate } = useContext(BotBuilderContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const isGoToIntentNode = (content: any): content is GoToIntentTreeNode => {
    return content instanceof GoToIntentTreeNode;
  };

  const goToIntentNode = isGoToIntentNode(data.node.content)
    ? data.node.content
    : null;

  const deleteGoToIntentNode = async (): Promise<void> => {
    onNodeDelete(data.node);
  };

  const handleLinkedIntentChange = useCallback(
    async (intentId: number | null): Promise<void> => {
      if (isGoToIntentNode(data.node.content) && chatbotId) {
        if (intentId) {
          try {
            const intentResponse = await BotBuilderService.getIntent(
              Number(chatbotId),
              intentId
            );
            data.node.content.intent = {
              id: intentResponse.id!,
              name: intentResponse.name,
              slots: intentResponse.slots
            };
            data.node.content.slotMappings = [];
          } catch {
            data.node.content.intent = { id: intentId, name: '', slots: [] };
          }
        } else {
          data.node.content.intent = null;
          data.node.content.slotMappings = [];
        }
        onNodeUpdate(data.node);
      }
    },
    [data.node, onNodeUpdate, chatbotId]
  );

  const handleSlotMappingsChange = (mappings: SlotMapping[]): void => {
    if (isGoToIntentNode(data.node.content)) {
      data.node.content.slotMappings = mappings;
      onNodeUpdate(data.node);
    }
  };

  return (
    <BotBuilderNode
      title='Go to question:'
      deleteButtonRef={deleteButtonRef}
      onNodeDelete={deleteGoToIntentNode}
      sourceNode={data.node}
      showAddButton={false}
      isLocked={data.isLocked}
    >
      <IntentsAutoComplete
        intentId={goToIntentNode?.intentId ?? null}
        intentName={goToIntentNode?.intent?.name ?? null}
        onSelectId={handleLinkedIntentChange}
        chatbotId={Number(chatbotId)}
        disabled={data.isLocked}
      />

      <SlotMappingConfiguration
        slotMappings={goToIntentNode?.slotMappings || []}
        onSlotMappingsChange={handleSlotMappingsChange}
        targetIntent={goToIntentNode?.intent ?? null}
        disabled={data.isLocked}
      />

      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -10 }}
        id={id}
        isConnectable={isConnectable}
      />
    </BotBuilderNode>
  );
}

export default GoToIntentNode;
